import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, FileText, Plus, Trash2, Download, Eye, Loader2, Send, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import ContactPicker, { PickedContact } from './ContactPicker';
import AddContactForm from './contacts/AddContactForm';

// הצעת מחיר — self-issued accounting document with a live preview. VAT is 0%
// (מלכ"ר). Numbering is allocated server-side on issue and locks after the
// first quote. The preview is the exact same HTML the PDF is rendered from.

interface LineItem {
  description: string;
  unitPrice: string;
  quantity: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP'];
const todayStr = () => new Date().toISOString().slice(0, 10);
const addDaysStr = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function CreatePriceQuoteDialog({ open, onClose, onCreated }: Props) {
  const [currency, setCurrency] = useState('ILS');
  const [client, setClient] = useState({ name: '', company: '', id: '', email: '', phone: '', address: '' });
  const [items, setItems] = useState<LineItem[]>([{ description: '', unitPrice: '', quantity: '1' }]);
  const [notes, setNotes] = useState('');
  const [issueDate, setIssueDate] = useState(todayStr());
  const [validUntil, setValidUntil] = useState(addDaysStr(30));

  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Post-issue "send to contact" step
  const [issued, setIssued] = useState<{ id: string; quoteNumber: number } | null>(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrency('ILS');
    setClient({ name: '', company: '', id: '', email: '', phone: '', address: '' });
    setItems([{ description: '', unitPrice: '', quantity: '1' }]);
    setNotes('');
    setIssueDate(todayStr());
    setValidUntil(addDaysStr(30));
    setMessage(null);
    setPreviewHtml('');
    setIssued(null);
    setSendEmail('');
    setSendMsg(null);

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/price-quotes/settings');
        if (cancelled) return;
        setNextNumber(res.data?.nextNumber ?? null);
        if (res.data?.validityDays != null) setValidUntil(addDaysStr(res.data.validityDays));
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const total = useMemo(
    () => items.reduce((s, it) => s + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 0), 0),
    [items]
  );

  const cleanItems = () => items
    .map((it) => ({ description: it.description.trim(), unitPrice: Number(it.unitPrice), quantity: Number(it.quantity) || 1 }))
    .filter((it) => it.description && Number.isFinite(it.unitPrice) && it.unitPrice >= 0);

  const updateItem = (idx: number, patch: Partial<LineItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, { description: '', unitPrice: '', quantity: '1' }]);
  const removeItem = (idx: number) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Autofill the client fields from an existing (supplier-category) contact.
  const fillFromContact = (c: PickedContact) => setClient((prev) => ({
    ...prev,
    name: c.name || prev.name,
    id: c.supplierTaxId || c.idNumber || prev.id,
    email: c.email || prev.email,
    phone: c.phone || prev.phone,
    address: [c.address, c.city].filter(Boolean).join(', ') || prev.address,
  }));

  // A new supplier contact was just created — pull its full record and autofill.
  const handleSupplierSaved = async (created?: { contactId?: string }) => {
    setShowAddSupplier(false);
    if (!created?.contactId) return;
    try {
      const res = await api.get(`/contacts/${created.contactId}`);
      const c = res.data || {};
      fillFromContact({
        _id: c._id,
        name: c.name || [c.firstName, c.lastName].filter(Boolean).join(' '),
        email: c.email,
        phone: c.phone,
        idNumber: c.idNumber,
        city: c.city,
        address: c.address,
        supplierTaxId: c.supplierProfile?.taxId,
      });
    } catch { /* non-fatal */ }
  };

  const buildPayload = () => ({
    client, notes: notes.trim() || undefined, currency, issueDate,
    validUntil: validUntil || undefined, items: cleanItems(),
  });

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post('/price-quotes/preview', buildPayload());
      setPreviewHtml(res.data?.html || '');
    } catch {
      setPreviewHtml('');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-refresh the preview (debounced) as the form changes.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void refreshPreview(); }, 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client, items, notes, currency, issueDate, validUntil]);

  const downloadPdf = async (id: string) => {
    try {
      const resp = await api.get(`/price-quotes/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setMessage({ type: 'error', text: 'ההצעה הונפקה אך לא ניתן להוריד את ה-PDF כרגע' });
    }
  };

  const handleSubmit = async () => {
    if (!client.name.trim()) { setMessage({ type: 'error', text: 'יש להזין שם לקוח' }); return; }
    if (!cleanItems().length) { setMessage({ type: 'error', text: 'יש להוסיף לפחות שורת פריט אחת עם תיאור ומחיר' }); return; }
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await api.post('/price-quotes', buildPayload());
      onCreated?.();
      // Move to the "issued → send to a contact?" step.
      setIssued({ id: res.data?.id, quoteNumber: res.data?.quoteNumber });
      setSendEmail(client.email || '');
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'שגיאה בהנפקת ההצעה' });
    } finally {
      setSubmitting(false);
    }
  };

  const sendQuote = async () => {
    if (!issued) return;
    const email = sendEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setSendMsg({ type: 'error', text: 'יש להזין כתובת מייל תקינה' }); return; }
    setSending(true);
    setSendMsg(null);
    try {
      await api.post(`/price-quotes/${issued.id}/send`, { email });
      setSendMsg({ type: 'success', text: `ההצעה נשלחה אל ${email}` });
    } catch (err: any) {
      setSendMsg({ type: 'error', text: err?.response?.data?.error || 'שגיאה בשליחת ההצעה' });
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-[#2C2C2C] rounded-lg text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" dir="rtl" onClick={onClose}>
      <div
        className="relative bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="text-teal-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">הצעת מחיר חדשה</h2>
            {nextNumber != null && (
              <span className="text-xs text-gray-500 dark:text-gray-400">· מספר הבא: {nextNumber}</span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300" aria-label="סגור">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] overflow-hidden">
          {/* ── Form ── */}
          <div className="p-4 overflow-auto space-y-4 border-l border-gray-200 dark:border-gray-700">
            {/* Client */}
            <div>
              <div className="text-xs uppercase text-gray-500 mb-2">פרטי הלקוח</div>
              <div className="mb-2">
                <ContactPicker
                  type="supplier"
                  placeholder="בחר לקוח/ספק קיים — הפרטים יתמלאו אוטומטית…"
                  onSelect={(c) => { if (c) fillFromContact(c); }}
                  onAddNew={() => setShowAddSupplier(true)}
                  addNewLabel="הוסף ספק חדש"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className={inputCls} placeholder="שם הלקוח *" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
                <input className={inputCls} placeholder="שם חברה (אופציונלי)" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} />
                <input className={inputCls} placeholder="ח.פ. / ת.ז." value={client.id} onChange={(e) => setClient({ ...client, id: e.target.value })} />
                <input className={inputCls} placeholder="אימייל" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
                <input className={inputCls} placeholder="טלפון" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
                <input className={inputCls} placeholder="כתובת" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} />
              </div>
            </div>

            {/* Dates + currency */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">תאריך</label>
                <input type="date" className={inputCls} value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">תקף עד</label>
                <input type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">מטבע</label>
                <select className={inputCls} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Items */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase text-gray-500">פריטים · מע"מ 0%</div>
                <button onClick={addItem} className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-semibold">
                  <Plus size={14} /> הוסף שורה
                </button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <input className={`${inputCls} col-span-6`} placeholder="תיאור" value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} />
                    <input className={`${inputCls} col-span-2`} type="number" min="0" placeholder="כמות" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                    <input className={`${inputCls} col-span-3`} type="number" min="0" placeholder="מחיר" value={it.unitPrice} onChange={(e) => updateItem(idx, { unitPrice: e.target.value })} />
                    <button onClick={() => removeItem(idx)} className="col-span-1 p-2 text-gray-400 hover:text-red-600" aria-label="מחק שורה">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-left mt-2 text-sm font-bold text-gray-900 dark:text-white">
                סה"כ: {currency === 'ILS' ? '₪' : ''}{total.toLocaleString()} {currency !== 'ILS' ? currency : ''}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">הערות (יופיעו על המסמך)</label>
              <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            {message && (
              <div className={`p-2 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              }`}>
                {message.text}
              </div>
            )}
          </div>

          {/* ── Live preview ── */}
          <div className="hidden lg:flex flex-col bg-gray-100 dark:bg-[#161616]">
            <div className="flex items-center justify-between px-4 py-2 text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <span className="flex items-center gap-1"><Eye size={14} /> תצוגה מקדימה</span>
              {previewLoading && <Loader2 size={14} className="animate-spin" />}
            </div>
            <div className="flex-1 overflow-auto p-3">
              {previewHtml ? (
                <iframe title="תצוגה מקדימה" srcDoc={previewHtml} className="w-full h-full min-h-[600px] bg-white rounded-lg shadow-sm border-0" />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-400">מלאו פרטים כדי לראות תצוגה מקדימה…</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center gap-2">
          <button onClick={refreshPreview} className="lg:hidden flex items-center gap-1 px-3 py-2 text-sm rounded-lg text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-950/30">
            <Eye size={16} /> תצוגה מקדימה
          </button>
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
            ביטול
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {submitting ? 'מנפיק…' : 'הנפק הצעת מחיר'}
          </button>
        </div>

        {/* Issued → send-to-contact overlay */}
        {issued && (
          <div className="absolute inset-0 z-10 bg-white/95 dark:bg-[#1E1E1E]/95 backdrop-blur-sm flex items-center justify-center p-6" dir="rtl">
            <div className="w-full max-w-md bg-white dark:bg-[#1E1E1E] rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl p-6 space-y-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 size={22} />
                <h3 className="text-lg font-bold">הצעת מחיר {issued.quoteNumber} הונפקה</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">שליחת ההצעה במייל ללקוח. ניתן לערוך את כתובת המייל לפני השליחה.</p>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">כתובת מייל</label>
                <input className={inputCls} type="email" placeholder="name@example.com" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} />
              </div>
              {sendMsg && (
                <div className={`p-2 rounded-lg text-sm ${
                  sendMsg.type === 'success'
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                }`}>{sendMsg.text}</div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={sendQuote} disabled={sending}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} שלח במייל
                </button>
                <button onClick={() => downloadPdf(issued.id)}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <Download size={16} /> הורד PDF
                </button>
                <div className="flex-1" />
                <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">סיום</button>
              </div>
            </div>
          </div>
        )}

        {/* Add a new supplier contact inline — opens the full add-contact form, type preset to ספק */}
        {showAddSupplier && (
          <AddContactForm
            initialType="supplier"
            onClose={() => setShowAddSupplier(false)}
            onSaved={handleSupplierSaved}
          />
        )}
      </div>
    </div>
  );
}
